<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /** Rendered width, in pixels or any valid CSS width value. Height scales automatically to match the graphic's aspect ratio. */
    size?: number | string
    /** Speed multiplier applied to every animation — 1 is the original speed, 2 is twice as fast, 0.5 is half. */
    speed?: number
  }>(),
  {
    size: 160,
    speed: 1,
  },
)

// SVG `dur` values below are expressed as `baseSeconds / speed` so a caller can speed up or
// slow down every animation in the graphic together via one prop, without touching the markup.
function dur(baseSeconds: number): string {
  return `${baseSeconds / props.speed}s`
}
</script>

<template>
  <svg
    id="e0D7ZBtOImG1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="-300 -340 600 400"
    shape-rendering="geometricPrecision"
    text-rendering="geometricPrecision"
    class="h-auto"
    :style="{ width: typeof size === 'number' ? `${size}px` : size }"
  >
    <defs>
      <radialGradient
        id="e0D7ZBtOImG6-fill"
        cx="0"
        cy="0"
        r="0.5"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0.5 0.5)"
      >
        <stop id="e0D7ZBtOImG6-fill-0" offset="0%" stop-color="#fa01fc" />
        <stop id="e0D7ZBtOImG6-fill-1" offset="100%" stop-color="rgba(250,1,252,0)" />
      </radialGradient>
      <radialGradient
        id="e0D7ZBtOImG7-fill"
        cx="0"
        cy="0"
        r="0.5"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0.5 0.5)"
      >
        <stop id="e0D7ZBtOImG7-fill-0" offset="0%" stop-color="#0054ff" />
        <stop id="e0D7ZBtOImG7-fill-1" offset="100%" stop-color="rgba(0,84,255,0)" />
      </radialGradient>
      <filter id="e0D7ZBtOImG8-filter" x="-150%" width="400%" y="-150%" height="400%">
        <feColorMatrix id="e0D7ZBtOImG8-filter-hue-rotate-0" type="hueRotate" values="0" result="result">
          <animate attributeName="values" values="0;360;0" :dur="dur(10)" repeatCount="indefinite" />
        </feColorMatrix>
      </filter>
      <linearGradient
        id="e0D7ZBtOImG11-fill"
        x1="0"
        y1="0.5"
        x2="1"
        y2="0.5"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0 0)"
      >
        <stop id="e0D7ZBtOImG11-fill-0" offset="0%" stop-color="#0054ff" />
        <stop id="e0D7ZBtOImG11-fill-1" offset="100%" stop-color="#fc0137" />
      </linearGradient>
      <radialGradient
        id="e0D7ZBtOImG13-fill"
        cx="0"
        cy="0"
        r="0.763019"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="matrix(0 -1 0.807221 0 0.934765 0.5)"
      >
        <stop id="e0D7ZBtOImG13-fill-0" offset="24%" stop-color="#000" />
        <stop id="e0D7ZBtOImG13-fill-1" offset="100%" stop-color="#fff" />
      </radialGradient>
      <radialGradient
        id="e0D7ZBtOImG14-fill"
        cx="0"
        cy="0"
        r="0.5"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0.5 0.5)"
      >
        <stop id="e0D7ZBtOImG14-fill-0" offset="0%" stop-color="#fa01fc" />
        <stop id="e0D7ZBtOImG14-fill-1" offset="100%" stop-color="rgba(252,1,55,0)" />
      </radialGradient>
      <radialGradient
        id="e0D7ZBtOImG15-fill"
        cx="0"
        cy="0"
        r="0.5"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0.5 0.5)"
      >
        <stop id="e0D7ZBtOImG15-fill-0" offset="0%" stop-color="#fff" />
        <stop id="e0D7ZBtOImG15-fill-1" offset="100%" stop-color="rgba(255,255,255,0)" />
      </radialGradient>
      <linearGradient
        id="e0D7ZBtOImG17-fill"
        x1="0.218881"
        y1="0.921618"
        x2="0.575964"
        y2="0.269703"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0 0)"
      >
        <stop id="e0D7ZBtOImG17-fill-0" offset="0%" stop-color="#000" />
        <stop id="e0D7ZBtOImG17-fill-1" offset="100%" stop-color="#d347ff" />
      </linearGradient>
      <linearGradient
        id="e0D7ZBtOImG19-fill"
        x1="0.218881"
        y1="0.921618"
        x2="0.575964"
        y2="0.269703"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0 0)"
      >
        <stop id="e0D7ZBtOImG19-fill-0" offset="0%" stop-color="#000" />
        <stop id="e0D7ZBtOImG19-fill-1" offset="100%" stop-color="#d347ff" />
      </linearGradient>
      <linearGradient
        id="e0D7ZBtOImG20-fill"
        x1="0.218881"
        y1="0.921618"
        x2="0.575964"
        y2="0.269703"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0 0)"
      >
        <stop id="e0D7ZBtOImG20-fill-0" offset="0%" stop-color="#000" />
        <stop id="e0D7ZBtOImG20-fill-1" offset="100%" stop-color="#d347ff" />
      </linearGradient>
      <radialGradient
        id="e0D7ZBtOImG27-fill"
        cx="0"
        cy="0"
        r="0.766616"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0.636744 0.566145)"
      >
        <stop id="e0D7ZBtOImG27-fill-0" offset="62%" stop-color="rgba(211,71,255,0)" />
        <stop id="e0D7ZBtOImG27-fill-1" offset="100%" stop-color="#fff" />
      </radialGradient>
      <radialGradient
        id="e0D7ZBtOImG30-fill"
        cx="0"
        cy="0"
        r="0.5"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0.5 0.5)"
      >
        <stop id="e0D7ZBtOImG30-fill-0" offset="0%" stop-color="#fff" />
        <stop id="e0D7ZBtOImG30-fill-1" offset="100%" stop-color="rgba(255,255,255,0)" />
      </radialGradient>
      <linearGradient
        id="e0D7ZBtOImG31-stroke"
        x1="1.01278"
        y1="0.912121"
        x2="-0.03944"
        y2="0.00449"
        spreadMethod="pad"
        gradientUnits="objectBoundingBox"
        gradientTransform="translate(0 0)"
      >
        <stop id="e0D7ZBtOImG31-stroke-0" offset="0%" stop-color="#fff" />
        <stop id="e0D7ZBtOImG31-stroke-1" offset="100%" stop-color="rgba(255,255,255,0)" />
      </linearGradient>
    </defs>
    <g transform="matrix(.68 0 0 0.68 10-140)">
      <g id="e0D7ZBtOImG3" opacity="0.5">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 0 0"
          to="360 0 0"
          :dur="dur(6)"
          repeatCount="indefinite"
        />
        <path
          id="e0D7ZBtOImG4"
          d="M200,0c.000001-109.316555-89.54305-200-200-200s-199.999999,89.885762-200,200"
          opacity="0.5"
          fill="none"
          stroke="#fff"
          stroke-width="2"
        />
        <path
          id="e0D7ZBtOImG5"
          d="M200,0c.000001-109.316555-89.54305-200-200-200s-199.999999,89.885762-200,200"
          transform="matrix(.866025-.5 0.5 0.866025 0 0)"
          opacity="0.5"
          fill="none"
          stroke="#fff"
          stroke-width="2"
        />
      </g>
      <g>
        <!-- Both orbit groups share this exact rotate (same from/to/dur, no phase offset) —
             combined with the two ellipses sitting at exactly opposite base positions below,
             that's what keeps them mirror-opposite at every instant, not just on average. A
             phase-shifted-but-different-position approach doesn't guarantee this: rotating two
             different points by the same amount doesn't make them opposite unless they already
             were, so the positions themselves (not the timing) are what carry the guarantee. -->
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 0 0"
          to="360 0 0"
          :dur="dur(20)"
          repeatCount="indefinite"
        />
        <ellipse
          rx="151"
          ry="151"
          transform="translate(134.256884-96.72358)"
          opacity="0.25"
          fill="url(#e0D7ZBtOImG6-fill)"
          stroke-width="0"
        >
          <animateTransform
            attributeName="transform"
            type="scale"
            values="1;1.1;1"
            :dur="dur(7)"
            repeatCount="indefinite"
            additive="sum"
          />
        </ellipse>
      </g>
      <g>
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 0 0"
          to="360 0 0"
          :dur="dur(20)"
          repeatCount="indefinite"
        />
        <ellipse
          rx="151"
          ry="151"
          transform="translate(-134.256884 96.72358)"
          opacity="0.38"
          fill="url(#e0D7ZBtOImG7-fill)"
          stroke-width="0"
        >
          <animateTransform
            attributeName="transform"
            type="scale"
            values="1;1.12;1"
            :dur="dur(8)"
            :begin="dur(-3)"
            repeatCount="indefinite"
            additive="sum"
          />
        </ellipse>
      </g>
      <g id="e0D7ZBtOImG8" mask="url(#e0D7ZBtOImG21)" filter="url(#e0D7ZBtOImG8-filter)">
        <ellipse
          rx="277.941173"
          ry="277.941173"
          transform="matrix(.68 0 0 0.68 0 0)"
          opacity="0.8"
          stroke-width="0"
        />
        <g mask="url(#e0D7ZBtOImG12)">
          <ellipse rx="168.09308" ry="168.09308" fill="url(#e0D7ZBtOImG11-fill)" stroke-width="0" />
          <mask id="e0D7ZBtOImG12" mask-type="luminance" x="-150%" y="-150%" height="400%" width="400%">
            <ellipse
              rx="168.09308"
              ry="168.09308"
              transform="matrix(0 1-1 0 0 0)"
              fill="url(#e0D7ZBtOImG13-fill)"
              stroke-width="0"
            />
          </mask>
        </g>
        <ellipse
          rx="277.941173"
          ry="277.941173"
          transform="matrix(.68 0 0 0.68 0-140)"
          fill="url(#e0D7ZBtOImG14-fill)"
          stroke-width="0"
        />
        <ellipse
          id="e0D7ZBtOImG15"
          rx="277.941173"
          ry="277.941173"
          transform="matrix(.68 0 0 0.68 0-60)"
          opacity="0.25"
          fill="url(#e0D7ZBtOImG15-fill)"
          stroke-width="0"
        >
          <animate attributeName="opacity" values="0.1;0.35;0.1" :dur="dur(4)" repeatCount="indefinite" />
        </ellipse>
        <g id="e0D7ZBtOImG16" transform="matrix(.965926-.258819 0.258819 0.965926 0 0)">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 0 0"
            to="360 0 0"
            :dur="dur(14)"
            repeatCount="indefinite"
            additive="sum"
          />
          <path
            id="e0D7ZBtOImG17"
            style="mix-blend-mode: screen"
            d="M-314.167894,121.222762c68.37375-47.951818,130.93536,25.721957,149.760721,77.555559c22.259285,61.288543,50.812996,116.56177,130.618732,125.507375-54.27346,20.352546-159.664992,17.881849-218.789885-43.249163s-61.589568-129.74749-61.589568-159.813771Z"
            transform="translate(33.788441-56.805063)"
            opacity="0.38"
            fill="url(#e0D7ZBtOImG17-fill)"
            stroke-width="1.024"
          />
          <g id="e0D7ZBtOImG18">
            <path
              id="e0D7ZBtOImG19"
              style="mix-blend-mode: screen"
              d="M-241.884025,-9.988618c7.711285,65.186627,87.944615,102.130341,151.937914,107.420456c81.948484,5.546737,139.16864-12.285676,188.827621,54.741996-27.378901,244.257403-276.603355,283.737326-387.205825,179.956012s-23.95015-300.030146,46.44029-342.118464Z"
              transform="translate(115.391394-166.06185)"
              opacity="0.349273"
              fill="url(#e0D7ZBtOImG19-fill)"
              stroke-width="1.024"
            />
          </g>
          <path
            id="e0D7ZBtOImG20"
            style="mix-blend-mode: screen"
            d="M-292.359856,81.636297c50.071889-13.817956,117.965055,48.774361,150.41758,86.565797c40.267513,44.471244,77.469889,77.688504,148.180351,104.157465-46.159374,87.904626-194.945289,98.090428-269.600964,24.091823s-50.233758-181.121756-28.996967-214.815085Z"
            transform="translate(74.589917-111.433457)"
            opacity="0.38"
            fill="url(#e0D7ZBtOImG20-fill)"
            stroke-width="1.024"
          />
        </g>
        <mask id="e0D7ZBtOImG21" mask-type="luminance" x="-150%" y="-150%" height="400%" width="400%">
          <g id="e0D7ZBtOImG22">
            <path
              id="e0D7ZBtOImG23"
              d="M-143.776898,0c0-79.405788,64.37111-143.776898,143.776898-143.776898s143.776899,64.37111,143.776899,143.776898-64.371111,143.776899-143.776899,143.776899-143.776898-64.371111-143.776898-143.776899Z"
              fill="#fff"
              stroke-width="0"
            />
            <path
              id="e0D7ZBtOImG24"
              d="M-150,0c0-82.842712,67.157288-150,150-150s150,67.157288,150,150-67.157288,150-150,150-150-67.157288-150-150Z"
              transform="matrix(1.093491 0 0 0.961682 0 0)"
              opacity="0.38"
              fill="#fff"
              stroke-width="0"
            />
            <path
              id="e0D7ZBtOImG25"
              d="M-150,0c0-82.842712,67.157288-150,150-150s150,67.157288,150,150-67.157288,150-150,150-150-67.157288-150-150Z"
              transform="matrix(.737871 0.737871-.69802 0.69802 0 0)"
              opacity="0.38"
              fill="#fff"
              stroke-width="0"
            />
            <path
              id="e0D7ZBtOImG26"
              d="M-150,0c0-82.842712,67.157288-150,150-150s150,67.157288,150,150-67.157288,150-150,150-150-67.157288-150-150Z"
              transform="matrix(0 1.043507-.987149 0 0 0)"
              opacity="0.38"
              fill="#fff"
              stroke-width="0"
            />
          </g>
        </mask>
      </g>
      <path
        id="e0D7ZBtOImG27"
        d="M-137.256263,0c0-75.80454,61.451723-137.256263,137.256263-137.256263s137.256263,61.451723,137.256263,137.256263-61.451723,137.256263-137.256263,137.256263-137.256263-61.451723-137.256263-137.256263Z"
        fill="url(#e0D7ZBtOImG27-fill)"
        stroke-width="0"
      >
        <animateTransform attributeName="transform" type="scale" values="1;1.06;1" :dur="dur(5)" repeatCount="indefinite" />
      </path>
      <g id="e0D7ZBtOImG28">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="360 0 0"
          to="0 0 0"
          :dur="dur(9)"
          repeatCount="indefinite"
        />
        <ellipse rx="200" ry="200" opacity="0.5" fill="none" stroke="#fff" stroke-width="2" />
        <ellipse
          id="e0D7ZBtOImG30"
          rx="39.46285"
          ry="39.46285"
          transform="translate(200 0)"
          opacity="0.2"
          fill="url(#e0D7ZBtOImG30-fill)"
          stroke-width="8"
          stroke-linecap="round"
        />
        <path
          id="e0D7ZBtOImG31"
          d="M0,-200c110.45695,0,200,89.54305,200,200"
          fill="none"
          stroke="url(#e0D7ZBtOImG31-stroke)"
          stroke-width="8"
          stroke-linecap="round"
          stroke-dasharray="0.01,14.26"
        />
        <path
          id="e0D7ZBtOImG32"
          d="M200,0c0,110.45695-89.54305,200-200,200s-200-89.54305-200-200"
          opacity="0.5"
          fill="none"
          stroke="#fff"
          stroke-width="2"
        />
        <path
          id="e0D7ZBtOImG33"
          d="M200,0c0,110.45695-89.54305,200-200,200s-200-89.54305-200-200"
          transform="matrix(.866025-.5 0.5 0.866025 0 0)"
          opacity="0.5"
          fill="none"
          stroke="#fff"
          stroke-width="2"
        />
      </g>
    </g>
  </svg>
</template>
